import { SelectedToolbarProps } from "../../../../ui/Table/Table";
import GenerateArticlesModal from "./GenerateArticlesModal";
import PublishArticlesModal from "./PublishArticlesModal";
import DeleteArticlesModal from "./DeleteArticlesModal";
import TableToolbar from "../../../../ui/Table/TableToolbar";
import { useCallback, useContext, useMemo } from "react";
import { useDisclosure } from "../../../../ui/util/useDisclosure";
import CancelArticlesGenerationModal from "./CancelArticlesGenerationModal";
import {
  Close,
  Download,
  Edit,
  PlayCircle,
  Send,
  Trash,
} from "../../../../ui/Icon/Icon";
import { graphql } from "../../../../__generated__/gql";
import { useMutation } from "urql";
import { useToast } from "../../../../ui/Toast/ToastContext";
import { format } from "date-fns";
import { ArticleStatus } from "../../../../__generated__/gql/graphql";
import UpdateArticlesModal from "./UpdateArticlesModal";
import ArticleTableContext from "./ArticleTableContext";

const ExportArticlesMutation = graphql(/* GraphQL */ `
  mutation exportArticles($filters: [ArticleFilter!]) {
    exportArticles(filters: $filters) {
      dataUri
    }
  }
`);

function ArticleTableToolbar({
  ids,
  isAllSelected,
  totalCount,
}: SelectedToolbarProps) {
  const { showToast } = useToast();
  const { filter, visibleRows, setRowSelection } =
    useContext(ArticleTableContext);
  const [, exportArticles] = useMutation(ExportArticlesMutation);
  const [generateArticlesModalOpened, generateArticlesModal] =
    useDisclosure(false);
  const [publishArticlesModalOpened, publishArticlesModal] =
    useDisclosure(false);
  const [deleteArticlesModalOpened, deleteArticlesModal] = useDisclosure(false);
  const [cancelArticlesGenerationModalOpened, cancelArticlesGenerationModal] =
    useDisclosure(false);
  const [updateArticlesModalOpened, updateArticlesModal] = useDisclosure(false);

  const onDownload = useCallback(async () => {
    const exportArticlesResponse = await exportArticles({
      filters: isAllSelected ? filter : [{ ids }],
    });

    if (!exportArticlesResponse.data) {
      return;
    }

    const element = document.createElement("a");
    element.href = exportArticlesResponse.data.exportArticles.dataUri;
    element.download = `macaw_${format(
      new Date(),
      "yyyy-MM-dd'T'HH:mm:ss",
    )}.csv`;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
    element.remove();

    showToast({
      title: "Your download is complete",
      description: "Check your downloads folder",
    });
  }, [exportArticles, filter, ids, isAllSelected, showToast]);

  const selectedRows = useMemo(
    () => visibleRows.filter((row) => ids.includes(row.id)),
    [ids, visibleRows],
  );

  const publishDisabled = useMemo(
    () =>
      !isAllSelected &&
      !selectedRows.some(
        (row) =>
          row.status === ArticleStatus.Generated ||
          row.status === ArticleStatus.Published,
      ),
    [isAllSelected, selectedRows],
  );

  const cancelDisabled = useMemo(
    () =>
      !isAllSelected &&
      !selectedRows.some((row) => row.status === ArticleStatus.Processing),
    [isAllSelected, selectedRows],
  );

  const generateDisabled = useMemo(
    () =>
      !isAllSelected &&
      selectedRows.every((row) => row.status == ArticleStatus.Processing),
    [isAllSelected, selectedRows],
  );

  return (
    <>
      <TableToolbar
        items={[
          {
            IconComponent: PlayCircle,
            onClick: generateArticlesModal.open,
            label: "Generate",
            disabled: generateDisabled,
          },
          {
            IconComponent: Send,
            onClick: publishArticlesModal.open,
            label: "Publish",
            disabled: publishDisabled,
          },
          {
            label: "More",
            items: [
              {
                label: "Edit",
                IconComponent: Edit,
                onClick: updateArticlesModal.open,
              },
              {
                label: "Download",
                IconComponent: Download,
                onClick: onDownload,
              },
              {
                label: "Delete",
                IconComponent: Trash,
                onClick: deleteArticlesModal.open,
              },
              {
                label: "Cancel generation",
                IconComponent: Close,
                onClick: cancelArticlesGenerationModal.open,
                disabled: cancelDisabled,
              },
            ],
          },
        ]}
      />

      <GenerateArticlesModal
        filters={isAllSelected ? filter : [{ ids }]}
        isOpen={generateArticlesModalOpened}
        onClose={generateArticlesModal.close}
      />
      <PublishArticlesModal
        filters={isAllSelected ? filter : [{ ids }]}
        isOpen={publishArticlesModalOpened}
        onClose={publishArticlesModal.close}
      />
      <DeleteArticlesModal
        filters={isAllSelected ? filter : [{ ids }]}
        numberOfArticles={isAllSelected ? totalCount : ids.length}
        isOpen={deleteArticlesModalOpened}
        onClose={deleteArticlesModal.close}
        onDelete={() => {
          if (setRowSelection) {
            setRowSelection((previous) => {
              const newValue = { ...previous };

              ids.forEach((id) => {
                delete newValue[id];
              });

              return newValue;
            });
          }
        }}
      />
      <CancelArticlesGenerationModal
        filters={isAllSelected ? filter : [{ ids }]}
        isOpen={cancelArticlesGenerationModalOpened}
        onClose={cancelArticlesGenerationModal.close}
      />
      <UpdateArticlesModal
        filters={isAllSelected ? filter : [{ ids }]}
        isOpen={updateArticlesModalOpened}
        onClose={updateArticlesModal.close}
      />
    </>
  );
}

export default ArticleTableToolbar;
